// // src/pages/AboutStickerPage.tsx
// import React from 'react';
// import Box from '@mui/material/Box';
// import Container from '@mui/material/Container';
// import Typography from '@mui/material/Typography';
// import Link from '@mui/material/Link';
// import List from '@mui/material/List';
// import ListItem from '@mui/material/ListItem';
// import ListItemText from '@mui/material/ListItemText';
// import ListItemIcon from '@mui/material/ListItemIcon';
// import CheckIcon from '@mui/icons-material/Check';
// import stickerInstructionImage from '../assets/images/sticker-instruction.png';

// const AboutStickerPage: React.FC = () => {
//   return (
//     <Container maxWidth="md" sx={{ mt: 5 }}>
//       <Typography variant="h4" component="h1" gutterBottom>
//         ReadLinkerシールの作成方法
//       </Typography>
//       <Typography variant="body1" paragraph>
//         以下のステップに従ってシールを作成してください：
//       </Typography>
//       <List>
//         <ListItem>
//           <ListItemIcon>
//             <CheckIcon />
//           </ListItemIcon>
//           <ListItemText
//             primary="PDFファイルをダウンロードします。"
//             secondary="シールの作成が完了すると、登録されているメールアドレスへDLリンクが送付されます。PDFファイルは複数ページになる場合があります。"
//           />
//         </ListItem>
//         <ListItem>
//           <ListItemIcon>
//             <CheckIcon />
//           </ListItemIcon>
//           <ListItemText
//             primary="印刷します。"
//             secondary={
//               <>
//                 シールの印刷用紙はエーワン製 A-one 31269 をお使いください。<br />
//                 <Link href="https://www.yodobashi.com/product/100000001000684729/" target="_blank" rel="noopener noreferrer">
//                   ヨドバシカメラの販売サイト
//                 </Link>
//                 <br />
//                 <Link href="https://www.biccamera.com/bc/item/1374437/" target="_blank" rel="noopener noreferrer">
//                   ビックカメラの販売サイト
//                 </Link>
//                 <br />
//                 <Link href="https://amzn.asia/d/5V4LaBa" target="_blank" rel="noopener noreferrer">
//                   Amazonの販売サイト(販売数量を確認してください)
//                 </Link>
//                 <br />
//                 <Link href="https://www.a-one.co.jp/product/search/detail.php?id=31269" target="_blank" rel="noopener noreferrer">
//                   エーワン公式サイト
//                 </Link>
//                 <br />
//                 家庭用プリンターでも印刷可能です。印刷する際には用紙サイズはA4、倍率を「100%」に設定してください。
//               </>
//             }
//           />
//         </ListItem>
//         <ListItem>
//           <ListItemIcon>
//             <CheckIcon />
//           </ListItemIcon>
//           <ListItemText
//             primary="本に貼ります。"
//             secondary="本の裏側などに貼っていただくのが使いやすいです。"
//           />
//         </ListItem>
//       </List>
//       <Box sx={{ textAlign: 'center', mt: 4 }}>
//         <img src={stickerInstructionImage} alt="シール作成指示" style={{ maxWidth: '100%', height: 'auto' }} />
//       </Box>
//     </Container>
//   );
// }

// export default AboutStickerPage;

import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import CheckIcon from '@mui/icons-material/Check';
import stickerInstructionImage from '../assets/images/sticker-instruction.png';
import { useAppTokens } from '../context/ThemeModeContext';

const AboutStickerPage: React.FC = () => {
  // ===== Style tokens (GraphPage準拠) =====
  const TOKENS = useAppTokens();

  const glassPanelSx = {
    background: TOKENS.glass,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 2,
    boxShadow: '0 0 0 rgba(0,0,0,0)',
    backdropFilter: 'blur(6px)',
  } as const;

  const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    px: 1.25,
    py: 0.75,
    borderRadius: 999,
    background: TOKENS.glassStrong,
    border: `1px solid ${TOKENS.border}`,
    color: TOKENS.text,
    fontSize: 12,
    lineHeight: 1,
    userSelect: 'none',
  } as const;

  return (
    <Box
      sx={{
        width: '100vw',
        minHeight: '100dvh',
        // ✅ スマホはページ全体スクロール（ネストスクロール回避）
        overflowY: { xs: 'auto', md: 'hidden' },
        WebkitOverflowScrolling: 'touch',
        background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
        color: TOKENS.text,
        position: 'relative',
      }}
    >
      {/* top status pill */}
      <Box
        sx={{
          position: 'absolute',
          top: 14,
          left: 14,
          zIndex: 5,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          flexWrap: 'wrap',
          pointerEvents: 'none',
        }}
      >
        <Box sx={pillSx}>How to</Box>
        <Box sx={{ ...pillSx, opacity: 0.9 }}>ReadLinkerシール</Box>
      </Box>

      <Container
        maxWidth="md"
        sx={{
          pt: 8,
          pb: 4,
          px: { xs: 2, md: 3 },
        }}
      >
        <Box
          sx={{
            ...glassPanelSx,
            // ✅ PCは“パネル固定 + パネル内スクロール”でもいいが、
            //   このページは内容が縦長なので、全デバイスで自然高さに寄せる方が安定。
            overflow: 'visible',
          }}
        >
          {/* header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${TOKENS.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
              ReadLinkerシールの作成方法
            </Typography>
            <Box sx={{ ...pillSx, opacity: 0.85 }}>3 steps</Box>
          </Box>

          <Box sx={{ px: 2, py: 2 }}>
            <Typography sx={{ fontSize: 13, color: TOKENS.textSub, mb: 1.5 }}>
              以下のステップに従ってシールを作成してください：
            </Typography>

            <List sx={{ py: 0 }}>
              <ListItem
                sx={{
                  alignItems: 'flex-start',
                  px: 0,
                  py: 1.25,
                  borderBottom: `1px solid ${TOKENS.border}`,
                }}
              >
                <ListItemIcon sx={{ minWidth: 34, mt: '2px', color: TOKENS.textSub }}>
                  <CheckIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ fontSize: 13, color: TOKENS.text }}>
                      PDFファイルをダウンロードします。
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mt: 0.5 }}>
                      シールの作成が完了すると、登録されているメールアドレスへDLリンクが送付されます。PDFファイルは複数ページになる場合があります。
                    </Typography>
                  }
                />
              </ListItem>

              <ListItem
                sx={{
                  alignItems: 'flex-start',
                  px: 0,
                  py: 1.25,
                  borderBottom: `1px solid ${TOKENS.border}`,
                }}
              >
                <ListItemIcon sx={{ minWidth: 34, mt: '2px', color: TOKENS.textSub }}>
                  <CheckIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ fontSize: 13, color: TOKENS.text }}>
                      印刷します。
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                        シールの印刷用紙はエーワン製 A-one 31269 をお使いください。
                      </Typography>

                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Link
                          href="https://www.yodobashi.com/product/100000001000684729/"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontSize: 12.5,
                            color: TOKENS.text,
                            textDecorationColor: TOKENS.accent,
                            '&:hover': { color: TOKENS.text },
                          }}
                        >
                          ヨドバシカメラの販売サイト
                        </Link>
                        <Link
                          href="https://www.biccamera.com/bc/item/1374437/"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontSize: 12.5,
                            color: TOKENS.text,
                            textDecorationColor: TOKENS.accent,
                            '&:hover': { color: TOKENS.text },
                          }}
                        >
                          ビックカメラの販売サイト
                        </Link>
                        <Link
                          href="https://amzn.asia/d/5V4LaBa"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontSize: 12.5,
                            color: TOKENS.text,
                            textDecorationColor: TOKENS.accent,
                            '&:hover': { color: TOKENS.text },
                          }}
                        >
                          Amazonの販売サイト(販売数量を確認してください)
                        </Link>
                        <Link
                          href="https://www.a-one.co.jp/product/search/detail.php?id=31269"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            fontSize: 12.5,
                            color: TOKENS.text,
                            textDecorationColor: TOKENS.accent,
                            '&:hover': { color: TOKENS.text },
                          }}
                        >
                          エーワン公式サイト
                        </Link>
                      </Box>

                      <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mt: 1.25 }}>
                        家庭用プリンターでも印刷可能です。印刷する際には用紙サイズはA4、倍率を「100%」に設定してください。
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>

              <ListItem sx={{ alignItems: 'flex-start', px: 0, py: 1.25 }}>
                <ListItemIcon sx={{ minWidth: 34, mt: '2px', color: TOKENS.textSub }}>
                  <CheckIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ fontSize: 13, color: TOKENS.text }}>
                      本に貼ります。
                    </Typography>
                  }
                  secondary={
                    <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mt: 0.5 }}>
                      本の裏側などに貼っていただくのが使いやすいです。
                    </Typography>
                  }
                />
              </ListItem>
            </List>

            <Box
              sx={{
                mt: 3,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${TOKENS.border}`,
                background: TOKENS.cardBg,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mb: 1 }}>
                参考画像
              </Typography>
              <img
                src={stickerInstructionImage}
                alt="シール作成指示"
                style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutStickerPage;
